import { ChevronRightIcon } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { cn } from "../../lib/utils";

const PRAXA_SIDEBAR_SECTION_STORAGE_PREFIX = "praxa:praxa-sidebar-section:";

function readPersistedExpanded(persistenceKey: string, fallback: boolean): boolean {
  if (typeof window === "undefined") return fallback;

  try {
    const stored = window.localStorage.getItem(
      `${PRAXA_SIDEBAR_SECTION_STORAGE_PREFIX}${persistenceKey}`,
    );
    if (stored === "expanded") return true;
    if (stored === "collapsed") return false;
  } catch {
    // Storage is an enhancement. The section remains usable when it is blocked.
  }

  return fallback;
}

function persistExpanded(persistenceKey: string, expanded: boolean): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      `${PRAXA_SIDEBAR_SECTION_STORAGE_PREFIX}${persistenceKey}`,
      expanded ? "expanded" : "collapsed",
    );
  } catch {
    // Keep the in-memory disclosure state when storage is blocked or full.
  }
}

export interface PraxaSidebarSectionProps {
  readonly actions?: ReactNode;
  readonly children: ReactNode;
  readonly className?: string;
  readonly contentClassName?: string;
  readonly defaultExpanded?: boolean;
  readonly persistenceKey: string;
  readonly title: string;
}

/**
 * A persistent sidebar disclosure with one native trigger.
 *
 * The title stays string-only, while optional actions render as siblings of
 * the disclosure button so interactive controls are never nested.
 */
export function PraxaSidebarSection({
  actions,
  children,
  className,
  contentClassName,
  defaultExpanded = true,
  persistenceKey,
  title,
}: PraxaSidebarSectionProps) {
  const reactId = useId();
  const contentId = `praxa-sidebar-section-${reactId}`;
  const triggerId = `${contentId}-trigger`;
  const [expanded, setExpanded] = useState(() =>
    readPersistedExpanded(persistenceKey, defaultExpanded),
  );

  function handleToggle() {
    setExpanded((current) => {
      const next = !current;
      persistExpanded(persistenceKey, next);
      return next;
    });
  }

  return (
    <section
      aria-labelledby={triggerId}
      className={cn("min-w-0", className)}
      data-praxa-sidebar-section=""
      data-state={expanded ? "expanded" : "collapsed"}
    >
      <div className="flex min-w-0 items-center">
        <h2 className="min-w-0 flex-1">
          <button
            aria-controls={contentId}
            aria-expanded={expanded}
            className="flex min-h-8 w-full cursor-pointer items-center gap-1.5 rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-xs font-medium text-sidebar-muted-foreground outline-hidden hover:text-sidebar-foreground focus-visible:ring-2 focus-visible:ring-ring"
            id={triggerId}
            onClick={handleToggle}
            type="button"
          >
            <ChevronRightIcon
              aria-hidden
              className={cn("size-3.5 shrink-0 transition-transform", expanded && "rotate-90")}
            />
            <span className="truncate">{title}</span>
          </button>
        </h2>
        {actions ? <div className="mr-1 flex shrink-0 items-center gap-1">{actions}</div> : null}
      </div>
      <div className={cn("min-w-0", contentClassName)} hidden={!expanded} id={contentId}>
        {children}
      </div>
    </section>
  );
}
